import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { authClient } from '../lib/authClient';
import PasswordInput from '../components/PasswordInput';
import { afterSignInPage } from '../utils/pendingInvite';
import ResendVerification from '../components/ResendVerification';

export default function SignIn() {
  const navigate = useNavigate();
  // Set by the reset-password page after a successful reset
  const notice = useLocation().state?.notice;
  const { refetch: refetchSession } = authClient.useSession();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  // Set when the account hasn't confirmed its email yet; signing in has just sent a new link
  const [unconfirmed, setUnconfirmed] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setUnconfirmed('');
    setLoading(true);
    try {
      const { error: authError } = await authClient.signIn.email({ email, password });
      if (authError?.code === 'EMAIL_NOT_VERIFIED') {
        setUnconfirmed(email.trim());
      } else if (authError?.status === 429) {
        setError('Too many attempts, try again in a few minutes.');
      } else if (authError) {
        setError(authError.message || 'Invalid email or password.');
      } else {
        // Load the new session before leaving: otherwise the dashboard's guard can still see the
        // old "signed out" state and bounce straight back here with an empty form
        await refetchSession();
        navigate(afterSignInPage());
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>Sign in</h1>
        {notice && <p style={styles.notice}>{notice}</p>}
        {unconfirmed && (
          <div style={styles.warning}>
            <p style={{ margin: '0 0 8px' }}>
              Please confirm your email first. We've just sent a new link to <strong>{unconfirmed}</strong>; it expires in an hour.
            </p>
            <ResendVerification email={unconfirmed} />
          </div>
        )}
        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="email"
            autoComplete="email"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            style={styles.input}
          />
          <PasswordInput
            autoComplete="current-password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            style={styles.input}
          />
          {error && <p style={styles.error}>{error}</p>}
          <button type="submit" disabled={loading} style={styles.button}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <p style={styles.forgot}>
          <Link to="/forgot-password" style={styles.link}>Forgot password?</Link>
        </p>
        <p style={styles.footer}>
          Don't have an account? <Link to="/sign-up" style={styles.link}>Sign up</Link>
        </p>
      </div>
    </div>
  );
}

// Shared with the forgot/reset password pages
export const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f5f5f5',
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: '40px 36px',
    width: '100%',
    maxWidth: 400,
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  title: {
    margin: '0 0 24px',
    fontSize: 24,
    fontWeight: 700,
    textAlign: 'center',
    color: '#1a1a1a',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  input: {
    padding: '12px 14px',
    borderRadius: 8,
    border: '1px solid #ddd',
    fontSize: 15,
    outline: 'none',
  },
  error: {
    color: '#dc3545',
    fontSize: 14,
    margin: 0,
  },
  button: {
    padding: '12px',
    borderRadius: 8,
    border: 'none',
    background: '#667eea',
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
  },
  footer: {
    textAlign: 'center',
    marginTop: 20,
    fontSize: 14,
    color: '#666',
  },
  notice: {
    background: '#e8f5e9',
    color: '#1b5e20',
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: 14,
    margin: '0 0 16px',
  },
  warning: {
    background: '#fff8e1',
    color: '#5d4200',
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: 14,
    lineHeight: 1.5,
    margin: '0 0 16px',
  },
  text: {
    fontSize: 14,
    color: '#444',
    lineHeight: 1.5,
    margin: '0 0 16px',
  },
  forgot: {
    textAlign: 'center',
    marginTop: 16,
    marginBottom: 0,
    fontSize: 14,
  },
  link: {
    color: '#667eea',
    textDecoration: 'none',
    fontWeight: 600,
  },
};
